import { defaultExplorerFilters } from '@/features/explorer/hooks/use-explorer-query-params';
import { AppliedFilterList } from '@/features/filters/components/applied-filter-list';
import { useLocale } from '@/lib/hooks/use-locale';
import type { ExplorerTopLevelEntity, ExplorerViewFilters } from '@/server/utils/explorer-utils';
import { getExplorerFilterKeyByTabName } from '@/server/utils/explorer-utils';
import { api } from '@/utils/api';
import React from 'react';

type Props = {
    tabsToShow: ExplorerTopLevelEntity[];
    selectedTab: ExplorerTopLevelEntity;
    filters: ExplorerViewFilters;
    onFiltersChange: (filters: ExplorerViewFilters) => void;
} & Omit<React.ComponentProps<typeof AppliedFilterList>, 'filters' | 'onRemoveAllFilters'>;

export const ExplorerViewAppliedFilters = ({
    selectedTab,
    tabsToShow,
    filters: queryFilters,
    onFiltersChange: setAppliedFilters,
    ...props
}: Props) => {
    const { t } = useLocale();

    const { data: filterLabels, isLoading } = api.platoUser.query.explorer.getFilterLabels.getLabels.useQuery(
        {
            filters: queryFilters,
        },
        {
            keepPreviousData: true,
        },
    );

    const appliedFilters = tabsToShow.flatMap((tab: ExplorerTopLevelEntity) => {
        const filterKey = getExplorerFilterKeyByTabName(tab);
        const appliedValues = (queryFilters[filterKey] || []) as string[];
        const options =
            filterLabels?.[filterKey]?.map((option) => ({
                label: option.name ?? '',
                value: option.id ?? '',
            })) ?? [];

        return appliedValues.map((value) => ({
            key: `${filterKey}-${value}`,
            category: t(`header_tab__${tab}`, {
                ns: 'explorer',
            }),
            filter: options.find((option) => option.value === value)?.label || '',
            isActive: tab === selectedTab,
            onRemove: () => {
                setAppliedFilters({
                    ...queryFilters,
                    [filterKey]: appliedValues.filter((v) => v !== value),
                });
            },
        }));
    });

    if (isLoading) {
        return null;
    }

    return (
        <AppliedFilterList
            filters={appliedFilters}
            onRemoveAllFilters={() => {
                setAppliedFilters(defaultExplorerFilters);
            }}
            {...props}
        />
    );
};
